import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Upload, Trash2, Tag, Save, Calculator } from 'lucide-react';
import { theme, cardStyle, inputStyle, btnStyle } from '../theme';

// ═══════════════════════════════════════════════════════════════════
//  RADIOGRAPHS (OPG / Ceph / STL / photos)
//  - Upload + list per patient
//  - OPG / photo: draw rectangle annotations with labels
//  - Ceph: place named landmarks → compute SNA/SNB/ANB etc.
//  - STL: preview as download link (3D viewer requires three.js — future)
// ═══════════════════════════════════════════════════════════════════

const TYPES = ['OPG','periapical','bitewing','cephalometric','occlusal','photo-intra','photo-extra','stl'];
const CEPH_LANDMARKS = ['Sella','Nasion','Orbitale','Porion','A','B','Menton','Gonion'];

// `lockedPatientId` is set when embedded inside PatientProfile — hides
// the patient picker and uses that patient's ID directly.
const RadiographsPage = ({ API_BASE, lockedPatientId = null }) => {
  const [patients, setPatients] = useState([]);
  const [patientId, setPatientId] = useState(lockedPatientId || '');
  const [images, setImages] = useState([]);
  const [selected, setSelected] = useState(null);
  const [uploadType, setUploadType] = useState('OPG');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    axios.get(`${API_BASE}/patients`).then(r => setPatients(r.data || []));
  }, [API_BASE]);

  const load = useCallback(async () => {
    if (!patientId) { setImages([]); return; }
    try {
      const r = await axios.get(`${API_BASE}/radiographs`, { params: { patientId } });
      setImages(r.data || []);
    } catch (e) { alert('Load failed: ' + (e.response?.data?.error || e.message)); }
  }, [API_BASE, patientId]);

  useEffect(() => { load(); }, [load]);

  const uploadFile = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) return alert('Choose a file first');
    if (!patientId) return alert('Select a patient first');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      fd.append('patientId', patientId);
      fd.append('type', uploadType);
      fd.append('takenDate', new Date().toISOString());
      await axios.post(`${API_BASE}/radiographs`, fd, { headers: { 'Content-Type':'multipart/form-data' } });
      fileRef.current.value = '';
      load();
    } catch (e) { alert('Upload failed: ' + (e.response?.data?.error || e.message)); }
    finally { setUploading(false); }
  };

  const deleteImage = async (id) => {
    if (!window.confirm('Delete this image?')) return;
    try {
      await axios.delete(`${API_BASE}/radiographs/${id}`);
      if (selected?._id === id) setSelected(null);
      load();
    } catch (e) { alert('Failed: ' + (e.response?.data?.error || e.message)); }
  };

  return (
    <div style={{ padding:20 }}>
      <h2 style={{ color: theme.navy, margin:'0 0 16px' }}>📷 Radiographs & Imaging</h2>

      <div style={{ ...cardStyle, marginBottom:12, display:'flex', gap:10, alignItems:'center', flexWrap:'wrap' }}>
        {!lockedPatientId && (
          <select value={patientId} onChange={e => setPatientId(e.target.value)} style={{ ...inputStyle, flex:1, minWidth:280 }}>
            <option value="">Select patient…</option>
            {patients.map(p => <option key={p._id} value={p._id}>{p.name} ({p.phone})</option>)}
          </select>
        )}
        <select value={uploadType} onChange={e => setUploadType(e.target.value)} style={{ ...inputStyle, width:160 }}>
          {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <input type="file" ref={fileRef} accept="image/*,.stl,.pdf" style={{ fontSize:13 }}/>
        <button onClick={uploadFile} disabled={uploading || !patientId} style={{ ...btnStyle, background: theme.primary, color:'white', display:'flex', gap:6, alignItems:'center' }}>
          <Upload size={14}/> {uploading ? 'Uploading…' : 'Upload'}
        </button>
      </div>

      {patientId && (
        <div style={{ display:'grid', gridTemplateColumns:'280px 1fr', gap:16 }}>
          {/* Gallery */}
          <div style={{ ...cardStyle, maxHeight:'70vh', overflowY:'auto' }}>
            <div style={{ fontWeight:700, color: theme.navy, marginBottom:10 }}>Gallery ({images.length})</div>
            {images.length === 0 ? <div style={{ color:'#888', fontSize:13, padding:10 }}>No images yet</div>
             : images.map(img => (
              <div key={img._id} onClick={() => setSelected(img)} style={{
                padding:8, borderRadius:8, marginBottom:6, cursor:'pointer',
                background: selected?._id === img._id ? theme.primaryLight : '#f9fafb',
                border: selected?._id === img._id ? `1px solid ${theme.primary}` : '1px solid transparent',
              }}>
                {img.mimeType?.startsWith('image/') && (
                  <img src={`${API_BASE}/radiographs/file/${img.fileName}`} alt="" style={{ width:'100%', height:100, objectFit:'cover', borderRadius:4, marginBottom:4 }}/>
                )}
                {!img.mimeType?.startsWith('image/') && (
                  <div style={{ height:80, background:'#e5e7eb', borderRadius:4, display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, color:'#666', marginBottom:4 }}>
                    {img.type.toUpperCase()}
                  </div>
                )}
                <div style={{ fontWeight:600, fontSize:12, color: theme.navy }}>{img.type}</div>
                <div style={{ fontSize:11, color:'#666' }}>{new Date(img.takenDate).toLocaleDateString('en-IN')}</div>
              </div>
            ))}
          </div>

          {/* Viewer */}
          <div>
            {!selected ? (
              <div style={{ ...cardStyle, padding:60, textAlign:'center', color:'#888' }}>
                Click an image to view and annotate
              </div>
            ) : selected.type === 'stl' ? (
              <STLViewer doc={selected} API_BASE={API_BASE} onDelete={() => deleteImage(selected._id)}/>
            ) : selected.type === 'cephalometric' ? (
              <CephViewer doc={selected} API_BASE={API_BASE} onUpdate={load} onDelete={() => deleteImage(selected._id)}/>
            ) : (
              <ImageViewer doc={selected} API_BASE={API_BASE} onUpdate={load} onDelete={() => deleteImage(selected._id)}/>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Generic image viewer with rect annotations ──
const ImageViewer = ({ doc, API_BASE, onUpdate, onDelete }) => {
  const [annotations, setAnnotations] = useState(doc.annotations || []);
  const [drawing, setDrawing] = useState(null);
  const [currentLabel, setCurrentLabel] = useState('Caries');
  const imgRef = useRef(null);

  useEffect(() => { setAnnotations(doc.annotations || []); }, [doc._id]);

  const onMouseDown = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    setDrawing({ shape:'rect', x, y, width:0, height:0, label: currentLabel, color:'#f59e0b' });
  };

  const onMouseMove = (e) => {
    if (!drawing) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    setDrawing({ ...drawing, width: x - drawing.x, height: y - drawing.y });
  };

  const onMouseUp = () => {
    if (drawing && Math.abs(drawing.width) > 0.01 && Math.abs(drawing.height) > 0.01) {
      const ann = { ...drawing, createdAt: new Date(), createdBy:'admin' };
      if (ann.width < 0) { ann.x += ann.width; ann.width = -ann.width; }
      if (ann.height < 0) { ann.y += ann.height; ann.height = -ann.height; }
      setAnnotations([...annotations, ann]);
    }
    setDrawing(null);
  };

  const save = async () => {
    try {
      await axios.put(`${API_BASE}/radiographs/${doc._id}`, { annotations });
      onUpdate();
    } catch (e) { alert('Save failed: ' + (e.response?.data?.error || e.message)); }
  };

  const removeAnnotation = (i) => setAnnotations(annotations.filter((_, idx) => idx !== i));

  return (
    <div style={cardStyle}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10, flexWrap:'wrap', gap:8 }}>
        <div>
          <b style={{ color: theme.navy }}>{doc.type}</b> · {doc.originalName}
        </div>
        <div style={{ display:'flex', gap:6, alignItems:'center' }}>
          <input value={currentLabel} onChange={e => setCurrentLabel(e.target.value)} placeholder="Label"
            style={{ ...inputStyle, width:140, fontSize:12 }}/>
          <button onClick={save} style={{ ...btnStyle, background: theme.success, color:'white' }}><Save size={14}/></button>
          <button onClick={onDelete} style={{ ...btnStyle, background: theme.danger, color:'white' }}><Trash2 size={14}/></button>
        </div>
      </div>
      <div style={{ position:'relative', userSelect:'none' }}
        onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp}>
        <img ref={imgRef} src={`${API_BASE}/radiographs/file/${doc.fileName}`} alt=""
          style={{ width:'100%', display:'block', cursor:'crosshair' }} draggable={false}/>
        {[...annotations, drawing].filter(Boolean).map((a, i) => (
          <div key={i} style={{
            position:'absolute',
            left: `${(a.x + (a.width < 0 ? a.width : 0)) * 100}%`,
            top:  `${(a.y + (a.height < 0 ? a.height : 0)) * 100}%`,
            width: `${Math.abs(a.width) * 100}%`,
            height: `${Math.abs(a.height) * 100}%`,
            border: `2px solid ${a.color || '#f59e0b'}`,
            background: 'rgba(245,158,11,0.15)',
            pointerEvents: i === annotations.length ? 'none' : 'auto',
          }} title={a.label}>
            <div style={{ position:'absolute', top:-20, left:0, background: a.color || '#f59e0b', color:'white', padding:'1px 6px', borderRadius:4, fontSize:10, fontWeight:700 }}>
              {a.label}
            </div>
          </div>
        ))}
      </div>
      <div style={{ marginTop:10 }}>
        <b style={{ fontSize:13, color: theme.navy }}>Annotations ({annotations.length})</b>
        {annotations.length > 0 && (
          <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginTop:6 }}>
            {annotations.map((a, i) => (
              <div key={i} style={{ padding:'3px 8px', background:'#f9fafb', border:'1px solid #e5e7eb', borderRadius:6, fontSize:12, display:'flex', gap:6, alignItems:'center' }}>
                <Tag size={12} color={a.color}/> {a.label}
                <span onClick={() => removeAnnotation(i)} style={{ cursor:'pointer', color: theme.danger, fontWeight:700 }}>×</span>
              </div>
            ))}
          </div>
        )}
        <div style={{ fontSize:11, color:'#888', marginTop:6 }}>Tip: click and drag on the image to add a rectangle annotation.</div>
      </div>
    </div>
  );
};

// ── Cephalometric viewer with landmark placement + angle computation ──
const CephViewer = ({ doc, API_BASE, onUpdate, onDelete }) => {
  const [landmarks, setLandmarks] = useState(doc.landmarks || []);
  const [nextLandmark, setNextLandmark] = useState(CEPH_LANDMARKS[0]);
  const [measurements, setMeasurements] = useState(doc.measurements || {});

  useEffect(() => {
    setLandmarks(doc.landmarks || []);
    setMeasurements(doc.measurements || {});
  }, [doc._id]);

  const onImgClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    const filtered = landmarks.filter(l => l.name !== nextLandmark);
    setLandmarks([...filtered, { name: nextLandmark, x, y }]);
    // Move to next unplaced
    const remaining = CEPH_LANDMARKS.find(n => !filtered.find(l => l.name === n) && n !== nextLandmark);
    if (remaining) setNextLandmark(remaining);
  };

  const save = async () => {
    try {
      await axios.put(`${API_BASE}/radiographs/${doc._id}`, { landmarks });
      onUpdate();
    } catch (e) { alert('Save failed: ' + (e.response?.data?.error || e.message)); }
  };

  const analyze = async () => {
    try {
      await save();
      const r = await axios.post(`${API_BASE}/radiographs/${doc._id}/ceph-analyze`);
      setMeasurements(r.data);
    } catch (e) { alert('Analysis failed: ' + (e.response?.data?.error || e.message)); }
  };

  return (
    <div style={cardStyle}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10, flexWrap:'wrap', gap:8 }}>
        <div>
          <b style={{ color: theme.navy }}>Cephalometric</b> · {doc.originalName}
        </div>
        <div style={{ display:'flex', gap:6 }}>
          <button onClick={save} style={{ ...btnStyle, background: theme.success, color:'white' }}><Save size={14}/></button>
          <button onClick={analyze} style={{ ...btnStyle, background: theme.primary, color:'white', display:'flex', gap:4, alignItems:'center' }}><Calculator size={14}/> Analyze</button>
          <button onClick={onDelete} style={{ ...btnStyle, background: theme.danger, color:'white' }}><Trash2 size={14}/></button>
        </div>
      </div>

      <div style={{ marginBottom:10 }}>
        <b style={{ fontSize:13, color: theme.navy }}>Place landmark:</b>
        <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginTop:6 }}>
          {CEPH_LANDMARKS.map(name => {
            const placed = landmarks.find(l => l.name === name);
            return (
              <button key={name} onClick={() => setNextLandmark(name)} style={{
                padding:'5px 10px', borderRadius:6, cursor:'pointer',
                border: `1px solid ${nextLandmark === name ? theme.primary : theme.border}`,
                background: nextLandmark === name ? theme.primary : placed ? '#dcfce7' : 'white',
                color: nextLandmark === name ? 'white' : placed ? '#166534' : theme.navy,
                fontSize:11, fontWeight:600,
              }}>
                {placed ? '✓' : '○'} {name}
              </button>
            );
          })}
        </div>
        <div style={{ fontSize:11, color:'#888', marginTop:6 }}>
          Currently placing: <b>{nextLandmark}</b> — click on image to mark.
        </div>
      </div>

      <div style={{ position:'relative', cursor:'crosshair' }} onClick={onImgClick}>
        <img src={`${API_BASE}/radiographs/file/${doc.fileName}`} alt="" style={{ width:'100%', display:'block' }} draggable={false}/>
        {landmarks.map((l, i) => (
          <div key={i} style={{
            position:'absolute', left: `${l.x * 100}%`, top: `${l.y * 100}%`,
            width:12, height:12, marginLeft:-6, marginTop:-6,
            borderRadius:'50%', background:'#ef4444', border:'2px solid white',
            boxShadow:'0 0 0 1px #ef4444', pointerEvents:'none',
          }}>
            <div style={{ position:'absolute', top:-20, left:14, background:'#111', color:'white', padding:'1px 6px', borderRadius:3, fontSize:10, fontWeight:700, whiteSpace:'nowrap' }}>
              {l.name}
            </div>
          </div>
        ))}
      </div>

      {Object.keys(measurements).length > 0 && (
        <div style={{ marginTop:16, padding:14, background:'#f0fdf4', borderLeft:`4px solid ${theme.success}`, borderRadius:8 }}>
          <b style={{ color: theme.navy }}>Cephalometric Analysis:</b>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10, marginTop:8 }}>
            {['SNA','SNB','ANB','FMA','MP_SN'].filter(k => measurements[k] != null).map(k => (
              <div key={k} style={{ padding:8, background:'white', borderRadius:6 }}>
                <div style={{ fontSize:10, color:'#666', fontWeight:600 }}>{k.replace('_', '-')}</div>
                <div style={{ fontSize:18, fontWeight:700, color: theme.navy }}>{measurements[k]}°</div>
                {measurements.interpretation?.[k] && (
                  <div style={{ fontSize:10, color:'#059669' }}>{measurements.interpretation[k]}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ── STL stub viewer ──
const STLViewer = ({ doc, API_BASE, onDelete }) => (
  <div style={cardStyle}>
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
      <b style={{ color: theme.navy }}>STL · {doc.originalName}</b>
      <button onClick={onDelete} style={{ ...btnStyle, background: theme.danger, color:'white' }}><Trash2 size={14}/></button>
    </div>
    <div style={{ padding:40, textAlign:'center', background:'#f9fafb', borderRadius:8 }}>
      <div style={{ fontSize:48, marginBottom:10 }}>🦷</div>
      <div style={{ color:'#666', marginBottom:12 }}>3D viewer requires Three.js (future enhancement)</div>
      <a href={`${API_BASE}/radiographs/file/${doc.fileName}`} download={doc.originalName}
        style={{ ...btnStyle, background: theme.primary, color:'white', textDecoration:'none', display:'inline-block' }}>
        ⬇ Download STL ({Math.round(doc.fileSizeKb / 1024 * 10) / 10} MB)
      </a>
      <div style={{ fontSize:12, color:'#888', marginTop:10 }}>
        Open the downloaded file in any STL viewer (e.g. 3D Builder, Meshmixer, online tools).
      </div>
    </div>
  </div>
);

export default RadiographsPage;
